/**
 * Shared types for the Error Config Viewer
 */

/**
 * Category of error configuration
 */
export type ErrorCategory =
  | "flights-quote"
  | "flights-fulfill"
  | "flights-purchase"
  | "flights-seats"
  | "lodging-quote"
  | "lodging-fulfill"
  | "lodging-purchase";

/**
 * Normalized error entry for display in the viewer
 */
export interface ErrorEntry {
  /** Category of the error (flights/lodging, quote/fulfill, etc.) */
  category: ErrorCategory;
  /** The error code enum value */
  errorCode: string;
  /** Translation key for the title */
  titleKey: string;
  /** Translation key for the subtitle */
  subtitleKey: string;
  /** Translation key for the primary button */
  primaryButtonKey: string;
  /** Action type for the primary button */
  primaryAction: string;
  /** Translation key for the secondary button (optional) */
  secondaryButtonKey?: string;
  /** Action type for the secondary button (optional) */
  secondaryAction?: string;
  /** CSS class name for styling/icons (e.g., "error", "warn", "missing", "timeout") */
  className?: string;
}

/**
 * Labels for each category
 */
export const CATEGORY_LABELS: Record<ErrorCategory, string> = {
  "flights-quote": "✈️ Flights - Cart Quote",
  "flights-fulfill": "✈️ Flights - Cart Fulfill",
  "flights-purchase": "✈️ Flights - Purchase Errors",
  "flights-seats": "✈️ Flights - Seat Selection",
  "lodging-quote": "🏨 Lodging - Cart Quote",
  "lodging-fulfill": "🏨 Lodging - Cart Fulfill",
  "lodging-purchase": "🏨 Lodging - Purchase Errors",
};

/**
 * Icon statistics for the viewer header
 */
export interface IconStats {
  missing: number;
  warn: number;
  timeout: number;
  error: number;
}

/**
 * Props for rendering an error preview
 */
export interface ErrorPreviewProps {
  /** The error entry to render */
  entry: ErrorEntry;
  /** Translated title */
  title: string;
  /** Translated subtitle */
  subtitle: string;
  /** Translated primary button text */
  primaryButtonText: string;
  /** Translated secondary button text (if any) */
  secondaryButtonText?: string;
  /** Whether the title translation is missing */
  titleMissing: boolean;
  /** Whether the subtitle translation is missing */
  subtitleMissing: boolean;
  /** Callback for primary button click */
  onPrimaryClick?: () => void;
  /** Callback for secondary button click */
  onSecondaryClick?: () => void;
  /** Callback to close the preview */
  onClose?: () => void;
}
